import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { DecimalUtil } from '@orca-so/common-sdk'
import { PublicKey } from '@solana/web3.js'
import Decimal from 'decimal.js'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

import Button from '@/components/base/Button'
import useAmplitude, { EVENT_NAMES } from '@/hooks/use-amplitude'
import { useRemitanoRate } from '@/hooks/use-settings'
import useTokens from '@/hooks/use-tokens'
import useTrans from '@/hooks/useTrans'
import { useNemoProgram } from '@/providers/nemo-program'
import { LiquidityPool } from '@/utils/apis/liquidity'
import { NOT_AVAILABLE_LOGO_URL, toResultAsync } from '@/utils/helpers'
import { format } from '@/utils/numeric'

import { calculatePoolApr, DAY_IN_SECONDS } from './helpers'

type PoolItemProps = {
  pool: LiquidityPool
}

const PoolItem = ({ pool }: PoolItemProps) => {
  const amplitude = useAmplitude()
  const trans = useTrans()
  const router = useRouter()
  const { data: rateData } = useRemitanoRate()

  const theme = useTheme()
  const { getTokenInfo } = useTokens()
  const sm = useMediaQuery(theme.breakpoints.up('sm'))
  const md = useMediaQuery(theme.breakpoints.up('md'))
  const { client: nemoProgramClient } = useNemoProgram()
  const poolAddress = pool.pool_address

  const { data: whirlPool, isLoading: isLoadingWhirlPool } = useQuery({
    queryKey: ['getPool', poolAddress],
    queryFn: async () => {
      const poolKey = new PublicKey(poolAddress)
      const [, whirlPoolData] = await toResultAsync(nemoProgramClient.getPool(poolKey))
      return whirlPoolData
    },
  })

  const poolRewardInfo = useMemo(() => {
    return whirlPool?.getRewardInfos()?.find((rewardInfo) => rewardInfo.initialized)
  }, [whirlPool])

  const poolRewardTokenInfo = useMemo(() => {
    if (!poolRewardInfo) return null
    return getTokenInfo(poolRewardInfo?.mint.toBase58())
  }, [poolRewardInfo, getTokenInfo])

  const emissionPerDay = useMemo(() => {
    if (!poolRewardInfo || !poolRewardTokenInfo) return null
    return DecimalUtil.fromU64(
      /** Ref: https://github.com/renec-chain/nemo-swap/blob/master/scripts/create_pool/10_set_pool_reward_emission.ts#L97C75-L97C75 */
      poolRewardInfo.emissionsPerSecondX64.muln(DAY_IN_SECONDS).shrn(64),
      poolRewardTokenInfo.decimals,
    )
  }, [poolRewardInfo, poolRewardTokenInfo])

  const emissionPerDayStr = useMemo(() => {
    return emissionPerDay ? format(emissionPerDay) : '0'
  }, [emissionPerDay])

  const fee24h = useMemo(() => {
    return new Decimal(pool.fee || 0)
      .div(100)
      .mul(pool.volumes_in_24h || 0)
  }, [pool])

  const aprValue = useMemo(() => {
    return format(
      calculatePoolApr({
        fee24h,
        totalLiquidity: Number(pool.total_liquidity),
        reward24h: emissionPerDay,
        rateData,
        rewardTokenInfo: poolRewardTokenInfo,
      }).toFixed(2),
      { decimals: 2 },
    )
  }, [fee24h, pool.total_liquidity, emissionPerDay, rateData, poolRewardTokenInfo])

  const elmTokenPair = (
    <Stack direction="row" alignItems="center" className="py-4 px-6 bg-[#1A2332] rounded-t-[16px]">
      <Stack direction="row">
        <img
          data-testid="img-token-a"
          src={pool.token_a_logo_url || NOT_AVAILABLE_LOGO_URL}
          alt={pool.token_a_name}
          className="rounded-full w-[40px] h-[40px] min-w-[40px] min-h-[40px] object-cover"
        />
        <img
          data-testid="img-token-b"
          src={pool.token_b_logo_url || NOT_AVAILABLE_LOGO_URL}
          alt={pool.token_b_name}
          className="relative left-[-8px] w-[40px] h-[40px] min-w-[40px] min-h-[40px] rounded-full object-cover"
        />
      </Stack>
      <Typography
        data-testid="txt-pool-pair-name"
        className="text-[20px] font-semibold text-[#C2CBD9]"
      >
        {pool.token_a_symbol} / {pool.token_b_symbol}
      </Typography>
    </Stack>
  )

  const elmFee = (
    <Typography className="text-xl font-bold text-white">
      {format(pool.fee)}%
    </Typography>
  )
  const elmApr = (
    <Typography className="text-xl font-bold text-white" data-testid="apr-value">
      {aprValue}%
    </Typography>
  )
  const elm24hVolume = (
    <Typography className="text-sm text-[#DDE0E5]">
      ${format(pool.volumes_in_24h, 6)}
    </Typography>
  )
  const elm24hFee = (
    <Typography className="text-sm text-[#DDE0E5]">
      ${format(fee24h.toFixed(6))}
    </Typography>
  )
  const elmTotalLiquidity = (
    <Typography className="text-sm text-[#DDE0E5]">
      ${format(pool.total_liquidity || 0, 3)}
    </Typography>
  )
  const elmDepositButton = (
    <Button
      data-testid="btn-goto-open-position"
      className="w-full"
      size="md"
      onClick={() => {
        amplitude.track(EVENT_NAMES.liquidity_list_select, {
          from_token: pool.token_a_symbol,
          to_token: pool.token_b_symbol,
          pool_address: poolAddress,
        })
        router.push(`/liquidity/open-position/${poolAddress}`)
      }}
    >
      {trans.liquidity.pool.deposit}
    </Button>
  )

  const elmRewardInfo = (
    <Typography className="text-center text-sm text-[#C2CBD9]" data-testid="reward-info">
      {trans.liquidity.pool.reward}:{' '}
      {emissionPerDayStr !== '0' && poolRewardTokenInfo ? (
        <>
          <span className="text-base font-bold leading-none text-[#10DC60]">
            +{emissionPerDayStr}
          </span>{' '}
          {poolRewardTokenInfo.displaySymbol}/{trans.liquidity.pool.day}
        </>
      ) : (
        <span className="text-base font-bold leading-none text-[#9CA3AE]">0</span>
      )}
    </Typography>
  )

  return (
    <Grid item data-testid={`pool-${poolAddress}`} xs={md ? 4 : sm ? 6 : 12}>
      {elmTokenPair}
      <Stack className="bg-[#1E2735] p-6 rounded-b-[16px] gap-4">
        <Stack direction="row">
          <Stack flex="1" direction="row" alignItems="center" gap="12px">
            <Box>
              <Typography data-testid="table-heading-pool-fee" className="text-sm text-[#9CA3AE]">
                {trans.home.commission}
              </Typography>
              {elmFee}
            </Box>
          </Stack>

          <Stack flex="1" direction="row" alignItems="center" gap="12px">
            <Box>
              <Typography data-testid="table-heading-pool-fee" className="text-sm text-[#9CA3AE]">
                APR
              </Typography>
              {elmApr}
            </Box>
          </Stack>
        </Stack>
        <Box className="py-4 border-t border-b border-solid border-[#3B4A5E]">
          <Table size="small" padding="none" className="">
            <TableBody>
              <TableRow>
                <TableCell className="border-none py-1.5">
                  <Typography
                    data-testid={`table-heading-pool-24h-volume-${poolAddress}`}
                    sx={{
                      fontSize: 14,
                      color: '#888C9E',
                    }}
                  >
                    {trans.liquidity.pool.volume_24h}
                  </Typography>
                </TableCell>
                <TableCell className="border-none py-1.5 pl-2 text-right">{elm24hVolume}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="border-none py-1.5">
                  <Typography
                    data-testid={`table-heading-pool-24h-fee-${poolAddress}`}
                    sx={{
                      fontSize: 14,
                      color: '#888C9E',
                    }}
                  >
                    {trans.liquidity.pool.fee_24h}
                  </Typography>
                </TableCell>
                <TableCell className="border-none py-1.5 pl-2 text-right">{elm24hFee}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="border-none py-1.5">
                  <Typography
                    data-testid="table-heading-pool-liquidity"
                    sx={{
                      fontSize: 14,
                      color: '#888C9E',
                    }}
                  >
                    {trans.liquidity.pool.total_liquidity}
                  </Typography>
                </TableCell>
                <TableCell className="border-none py-1.5 pl-2 text-right">
                  {elmTotalLiquidity}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Stack gap="8px" alignItems="center">
          {isLoadingWhirlPool ? (
            <Skeleton
              height={16}
              width="70%"
              className="transform-none inline-block"
              data-testid="skeleton-reward-info"
            />
          ) : (
            elmRewardInfo
          )}
          {elmDepositButton}
        </Stack>
      </Stack>
    </Grid>
  )
}

export default PoolItem
